<!--
  Angular 12 port of ui/app/components/efile/efileElements/pdf-upload.html.

  Preserved behaviour:
    - Upload form hidden when SUBMISSION_STATUS === 'Production Accepted'.
    - Signature-document toggle renders with the same visual "switch" chrome
      that the legacy template paints via ng-model + CSS (.switch / .slider).
    - "Other" file-type branch reveals an extra free-text input.
    - File-name field is always read-only — the controller computes it.
    - Upload button is disabled until {file, comments, fileName} are all set.
    - Per-row view/delete actions swap for an inline spinner while the action
      is in flight.
    - Delete action is hidden once the submission goes production-accepted
      (legacy pdf-upload.html L1941 `ng-if`).
-->

<section class="pdf-upload">
  @if (canShowUploadForm) {
<form class="pdf-upload__form" (ngSubmit)="uploadPdf()">
    <div class="display-flex mt-10">
      <div class="label-item">
        <label>Signature Document:</label>
      </div>
      <div class="form-item">
        <label class="switch gtw-red">
          <input
            type="checkbox"
            [(ngModel)]="isSignatureDocument"
            name="isSignatureDocument"
            (change)="onSignatureToggle()"
          />
          <span class="slider round"></span>
        </label>
      </div>
    </div>

    <div class="display-flex mt-10">
      <div class="label-item">
        <label>File Type:</label>
      </div>
      <div class="form-item">
        <select
          class="form-control"
          name="fileType"
          [(ngModel)]="fileType"
          (change)="fileTypeChanged()"
        >
          <option value="">Select FileType</option>
          @for (opt of fileTypeList; track $index) {
<option [value]="opt.ID">{{ opt.NAME }}</option>
}
        </select>
      </div>
    </div>

    @if (showOtherField) {
<div class="display-flex mt-10">
      <div class="label-item">
        <label>Enter File Type:</label>
      </div>
      <div class="form-item">
        <input
          class="form-control"
          type="text"
          name="otherFileType"
          [(ngModel)]="otherFileType"
          (change)="onOtherFileTypeChange()"
        />
      </div>
    </div>
}

    <div class="display-flex mt-10">
      <div class="label-item">
        <label>File Name:</label>
      </div>
      <div class="form-item">
        <input
          class="form-control"
          type="text"
          name="fileName"
          [ngModel]="fileName"
          readonly
        />
      </div>
    </div>

    <div class="display-flex mt-10">
      <div class="label-item">
        <label>Comments:</label>
      </div>
      <div class="form-item">
        <input
          class="form-control"
          type="text"
          name="comments"
          [(ngModel)]="comments"
          [readonly]="readOnlyField"
        />
      </div>
    </div>

    <div class="display-flex mt-10">
      <div class="label-item">
        <label>File:</label>
      </div>
      <div class="form-item">
        <input
          #fileInput
          class="pdf-upload__file-input"
          type="file"
          accept=".pdf"
          name="pdfFile"
          [disabled]="isSignatureDocument && !canShowUploadForm"
          (change)="onFileSelected($event)"
        />
      </div>
    </div>

    <div class="display-flex mt-10">
      <button
        type="submit"
        class="btn btn-info"
        [disabled]="!canUpload"
      >
        Upload
        @if (uploading) {
<span>
          <i class="fa fa-spin fa-spinner" aria-hidden="true"></i>
        </span>
}
      </button>
    </div>
  </form>
}

  @if (!canShowUploadForm) {
<div
   
    class="pdf-upload__read-only-banner"
    role="status"
  >
    Submission is <strong>Production Accepted</strong>. Uploads and deletions
    are disabled for this submission.
  </div>
}

  <div class="table-container">
    @if (loadingGrid) {
<div class="loader">
      <i class="fa fa-spinner fa-spin fa-4x" aria-hidden="true"></i>
    </div>
}

    @if (!loadingGrid && loadError) {
<div class="pdf-upload__error">
      {{ loadError }}
    </div>
}

    @if (!loadingGrid && !loadError) {
<div
     
      class="pdf-upload__toolbar"
    >
      <div class="pdf-upload__search">
        <i class="fa fa-search pdf-upload__search-icon" aria-hidden="true"></i>
        <input
          type="text"
          class="form-control pdf-upload__search-input"
          name="pdfSearch"
          placeholder="Search..."
          [ngModel]="searchQuery"
          (ngModelChange)="onSearchChange($event)"
          aria-label="Search uploaded PDFs"
        />
        @if (searchQuery) {
<i
         
          class="fa fa-times pdf-upload__search-clear"
          role="button"
          tabindex="0"
          title="Clear search"
          aria-label="Clear search"
          (click)="onSearchChange('')"
          (keyup.enter)="onSearchChange('')"
        ></i>
}
      </div>
    </div>
}

    @if (!loadingGrid && !loadError) {
<div class="pdf-upload__table-scroll">
      <table class="table table-striped table-bordered pdf-upload__table">
        <thead>
          <tr>
            <th scope="col"
                class="sortable"
                [class.sorted]="sortKey === 'FILE_NAME'"
                (click)="toggleSort('FILE_NAME')"
                (keyup.enter)="toggleSort('FILE_NAME')"
                tabindex="0"
                role="button"
                aria-label="Sort by File Name">
              <span>File Name</span>
              <i class="sort-icon" [ngClass]="sortIconClass('FILE_NAME')" aria-hidden="true"></i>
            </th>
            <th scope="col"
                class="sortable"
                [class.sorted]="sortKey === 'COMMENTS'"
                (click)="toggleSort('COMMENTS')"
                (keyup.enter)="toggleSort('COMMENTS')"
                tabindex="0"
                role="button"
                aria-label="Sort by Comments">
              <span>Comments</span>
              <i class="sort-icon" [ngClass]="sortIconClass('COMMENTS')" aria-hidden="true"></i>
            </th>
            <th scope="col"
                class="sortable"
                [class.sorted]="sortKey === 'USER_NAME'"
                (click)="toggleSort('USER_NAME')"
                (keyup.enter)="toggleSort('USER_NAME')"
                tabindex="0"
                role="button"
                aria-label="Sort by Created By">
              <span>Created By</span>
              <i class="sort-icon" [ngClass]="sortIconClass('USER_NAME')" aria-hidden="true"></i>
            </th>
            <th scope="col"
                class="sortable"
                [class.sorted]="sortKey === 'FORMATTED_CREATE_DATE'"
                (click)="toggleSort('FORMATTED_CREATE_DATE')"
                (keyup.enter)="toggleSort('FORMATTED_CREATE_DATE')"
                tabindex="0"
                role="button"
                aria-label="Sort by Created Date">
              <span>Created Date</span>
              <i class="sort-icon" [ngClass]="sortIconClass('FORMATTED_CREATE_DATE')" aria-hidden="true"></i>
            </th>
            <th scope="col" class="text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          @if (filteredPdfs.length === 0) {
<tr>
            <td colspan="5" class="pdf-upload__empty">
              @if (uploadedPdfs.length === 0) {

                No PDFs uploaded yet.
              
} @else {

                No PDFs match "{{ searchQuery }}".
              
}
              
            </td>
          </tr>
}
          @for (row of filteredPdfs; track trackByRow($index, row)) {
<tr>
            <td>{{ row.FILE_NAME }}</td>
            <td>{{ row.COMMENTS }}</td>
            <td>{{ row.USER_NAME }}</td>
            <td>{{ row.FORMATTED_CREATE_DATE }}</td>
            <td class="text-center">
              @if (!row.loadingPdf) {

                <i
                  class="fa fa-file-pdf-o"
                  role="button"
                  tabindex="0"
                  title="View PDF"
                  aria-label="View PDF"
                  (click)="viewPdf(row)"
                  (keyup.enter)="viewPdf(row)"
                ></i>
                @if (canDeleteRows()) {
<i
                 
                  class="fa fa-remove"
                  role="button"
                  tabindex="0"
                  title="Delete PDF"
                  aria-label="Delete PDF"
                  (click)="deletePdf(row)"
                  (keyup.enter)="deletePdf(row)"
                ></i>
}
              
}
              @if (row.loadingPdf) {
<div>
                <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
              </div>
}
            </td>
          </tr>
}
        </tbody>
      </table>
    </div>
}
  </div>
</section>
